import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import {
  fmtTime, gameNameById, teamLabel,
  PlayersBadge, StatPill, GameFilterDropdown, ScoreboardBackground
} from "./ScoreboardShared";
import { hashCode } from "../../lib/scoreboardUtils";

const API = process.env.REACT_APP_API_URL;

function makeSessionMetricsFallback(s) {
  const key = [
    s.gameId,
    s.finishedAt || "",
    s.playedDuration || 0,
    teamLabel(s),
    (s.players || []).map((p) => p.pseudo).join("|"),
  ].join(":");

  const seed = hashCode(key);

  const l1 = seed % 4; // 0..3
  const l2 = (seed >> 2) % 4; // 0..3
  const l3 = (seed >> 4) % 3; // 0..2
  const answer = (seed >> 6) % 2; // 0..1 (mock réponses)
  const hintsTotal = l1 + l2 + l3;

  const errors = (seed >> 8) % 10; // 0..9

  const played = s.playedDuration || 0;
  const timeScore = Math.max(0, 6000 - Math.floor(played * 6));
  const penaltyHints = l1 * 40 + l2 * 90 + l3 * 160 + answer * 220;
  const penaltyErrors = errors * 120;

  const points = Math.max(0, Math.round(timeScore - penaltyHints - penaltyErrors));

  return {
    hints: { l1, l2, l3, total: hintsTotal },
    answers: answer,
    errors,
    points,
  };
}

export default function CompanyScoreboardBySessionCode() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  const [gameFilter, setGameFilter] = useState("all");
  const [metricsBySessionId, setMetricsBySessionId] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setErr(null);
      setData(null);
      setMetricsBySessionId(null);

      const qs = gameFilter === "all" ? "" : `?gameId=${gameFilter}`;

      try {
        const res = await axios.get(
          `${API}/api/scoreboards/company/by-session/${code}${qs}`
        );

        const payload = res.data;
        if (cancelled) return;

        setData(payload);

        const sessions = payload?.sessions || [];
        const ids = sessions.map((s) => s.id).filter(Boolean);

        if (!ids.length) {
          setMetricsBySessionId({});
          return;
        }

        const metricsRes = await axios.get(`${API}/api/metrics/summary`, {
          params: {
            sessionIds: ids.join(","),
            metricKeys: "errors,hints_l1,hints_l2,hints_l3,hints_answer",
          },
        });

        if (!cancelled) {
          setMetricsBySessionId(metricsRes.data?.bySessionId || {});
        }
      } catch (e) {
        if (!cancelled) setErr(e.response?.data?.message || "Erreur");
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [code, gameFilter]);

  const sessionsRanked = useMemo(() => {
    const sessions = data?.sessions || [];

    const enriched = sessions.map((s, idx) => {
      const fallback = makeSessionMetricsFallback(s);

      const sid = String(s.id);
      const real = metricsBySessionId?.[sid] || {};

      const rl1 = typeof real?.hints_l1 === "number" ? real.hints_l1 : null;
      const rl2 = typeof real?.hints_l2 === "number" ? real.hints_l2 : null;
      const rl3 = typeof real?.hints_l3 === "number" ? real.hints_l3 : null;
      const rans =
        typeof real?.hints_answer === "number" ? real.hints_answer : null;
      const rerr = typeof real?.errors === "number" ? real.errors : null;

      const hints = {
        l1: rl1 != null ? rl1 : fallback.hints.l1,
        l2: rl2 != null ? rl2 : fallback.hints.l2,
        l3: rl3 != null ? rl3 : fallback.hints.l3,
      };

      const answers = rans != null ? rans : fallback.answers;
      const errors = rerr != null ? rerr : fallback.errors;

      const hintsTotal = hints.l1 + hints.l2 + hints.l3;

      // Fallback : si pas de points, on affiche le mock pour éviter un 0 "injuste"
      const points =
        typeof s.points === "number" ? s.points : fallback.points;

      return {
        ...s,
        _metrics: {
          hints: { ...hints, total: hintsTotal },
          answers,
          errors,
          points,
        },
        _key: `${idx}-${s.finishedAt || ""}-${s.playedDuration || 0}-${teamLabel(
          s
        )}`,
      };
    });

    enriched.sort((a, b) => {
      const pa = a._metrics?.points ?? 0;
      const pb = b._metrics?.points ?? 0;
      if (pb !== pa) return pb - pa;

      // tie-breaker: temps croissant
      const ta = a.playedDuration ?? Number.MAX_SAFE_INTEGER;
      const tb = b.playedDuration ?? Number.MAX_SAFE_INTEGER;
      return ta - tb;
    });

    return enriched;
  }, [data, metricsBySessionId]);

  const podium = sessionsRanked.slice(0, 3);
  const list = sessionsRanked.slice(3, 50);

  const formulaText = useMemo(() => {
    return [
      "Ce classement est trié sur les points enregistrés en base (session.points).",
      "Les badges Indices/Réponses/Erreurs viennent des métriques (pour le détail).",
      "Tri: points décroissants (puis temps croissant).",
    ];
  }, []);

  if (err) return <div className="p-6 text-rose-300">❌ {err}</div>;
  if (!data) return <div className="p-6 text-slate-200">Chargement…</div>;

  return (
    <div className="min-h-[calc(100vh-85px)] bg-[#070712] text-white">
     <ScoreboardBackground />

      <div className="relative max-w-7xl mx-auto px-4 py-10">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-[0.28em] text-slate-400">
              Classement interne
            </p>
            <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">
              Scoreboard entreprise —{" "}
              <span className="text-fuchsia-300">{data.company?.name}</span>
            </h1>
            <p className="mt-2 text-sm text-slate-300">
              Compare les points de ton équipe avec les autres équipes de ton entreprise ! C'est l'occasion de frimer à la pause café.

            </p>
          </div>

          <div className="mt-2 md:mt-0 flex flex-col sm:flex-row gap-3 items-start sm:items-center">
            <GameFilterDropdown value={gameFilter} onChange={setGameFilter} />
            <Link
              to="/scoreboards"
              className="rounded-2xl px-4 py-2 text-sm font-semibold bg-white/5 border border-white/10 hover:bg-white/10 transition"
            >
              ← Retour leaderboard
            </Link>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
          <section className="lg:col-span-8 space-y-6">
            <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur p-5">
              <div className="flex items-center justify-between">
                <p className="text-[11px] uppercase tracking-[0.22em] text-slate-400">
                  Podium entreprise
                </p>
                <p className="text-xs text-slate-400">classement points</p>
              </div>

              {podium.length === 0 ? (
                <p className="mt-4 text-sm text-slate-300">
                  Aucune session terminée pour cette entreprise.
                </p>
              ) : (
                <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4">
                  {podium.map((s, idx) => {
                    const rank = idx + 1;
                    const medal = rank === 1 ? "🥇" : rank === 2 ? "🥈" : "🥉";
                    const highlight =
                      rank === 1
                        ? "border-fuchsia-400/40 shadow-[0_0_28px_rgba(217,70,239,0.20)]"
                        : "border-white/10";

                    const m = s._metrics;

                    return (
                      <div
                        key={`pod-${s._key}`}
                        className={[
                          "rounded-2xl border bg-black/30 p-4",
                          highlight,
                        ].join(" ")}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-center gap-2">
                            <span className="text-2xl">{medal}</span>
                            <span className="rounded-xl border border-white/10 bg-white/5 px-2 py-1 text-xs font-extrabold text-slate-200">
                              #{rank}
                            </span>
                          </div>

                          <div className="text-right">
                            <p className="text-lg font-extrabold text-emerald-200 whitespace-nowrap">
                              {m.points} pts
                            </p>
                            <p className="text-[11px] font-semibold text-cyan-200 whitespace-nowrap">
                              {fmtTime(s.playedDuration || 0)}
                            </p>
                          </div>
                        </div>

                        <p className="mt-3 text-base font-extrabold text-slate-100">
                          {teamLabel(s)}
                        </p>

                        <p className="mt-1 text-[11px] text-slate-400 line-clamp-2">
                          {(s.players || []).map((p) => p.pseudo).join(", ")}
                        </p>

                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          <PlayersBadge n={s.nbPlayers} />
                          <span className="text-[11px] text-slate-500">
                            {gameNameById(s.gameId)}
                          </span>

                          <span className="h-1 w-1 rounded-full bg-white/20" />

                          <StatPill
                            label="Indices"
                            value={m.hints.total}
                            kind="hints"
                          />
                          <StatPill
                            label="Réponses"
                            value={m.answers}
                            kind="answers"
                          />
                          <StatPill
                            label="Erreurs"
                            value={m.errors}
                            kind="errors"
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* sessions list */}
            <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur p-5">
              <div className="flex items-center justify-between">
                <p className="text-[11px] uppercase tracking-[0.22em] text-slate-400">
                  Sessions
                </p>
                <p className="text-xs text-slate-400">
                  {list.length ? `${list.length} sessions (hors podium)` : "—"}
                </p>
              </div>

              <div className="mt-4 space-y-2">
                {list.map((s, idx) => {
                  const m = s._metrics;
                  const rank = idx + 4;

                  return (
                    <div
                      key={`session-${s._key}`}
                      className="rounded-2xl border border-white/10 bg-black/30 px-4 py-3 flex items-center justify-between gap-4"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="h-9 w-9 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center font-extrabold">
                          {rank}
                        </div>

                        <div className="min-w-0">
                          <p className="text-sm font-extrabold">
                            {teamLabel(s)}
                          </p>

                          <p className="mt-1 text-[11px] text-slate-400 line-clamp-1">
                            {(s.players || []).map((p) => p.pseudo).join(", ")}
                          </p>

                          <div className="mt-1 flex flex-wrap items-center gap-2">
                            <PlayersBadge n={s.nbPlayers} />
                            <span className="text-[11px] text-slate-500">
                              {gameNameById(s.gameId)}
                            </span>
                            {s.finishedAt ? (
                              <span className="text-[11px] text-slate-500">
                                · {new Date(s.finishedAt).toLocaleDateString()}
                              </span>
                            ) : null}

                            <span className="h-1 w-1 rounded-full bg-white/20" />

                            <StatPill
                              label="Indices"
                              value={m.hints.total}
                              kind="hints"
                            />
                            <StatPill
                              label="Réponses"
                              value={m.answers}
                              kind="answers"
                            />
                            <StatPill
                              label="Erreurs"
                              value={m.errors}
                              kind="errors"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        <div className="text-sm font-extrabold text-emerald-200 whitespace-nowrap">
                          {m.points} pts
                        </div>
                        <div className="text-[11px] font-semibold text-cyan-200 whitespace-nowrap">
                          {fmtTime(s.playedDuration || 0)}
                        </div>
                      </div>
                    </div>
                  );
                })}

                {list.length === 0 && (
                  <p className="text-sm text-slate-300">
                    Aucune session terminée pour cette entreprise.
                  </p>
                )}
              </div>
            </div>
          </section>

          {/* aside */}
          <aside className="lg:col-span-4 space-y-6">
            <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur p-5">
              <p className="text-[11px] uppercase tracking-[0.22em] text-slate-400">
                Comment lire le score
              </p>

              <div className="mt-4 space-y-3">
                <div className="rounded-2xl border border-white/10 bg-black/30 p-4">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400">
                    Objectif
                  </p>
                  <p className="mt-1 text-sm text-slate-300">
                    Marquer un maximum de points en allant vite, avec le moins d’erreurs et d’indices possible.
                  </p>
                </div>
                <div className="rounded-2xl border border-white/10 bg-black/30 p-4">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400">
                    Ce qui influence
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="rounded-xl border border-cyan-400/20 bg-cyan-500/10 px-3 py-2 text-[12px] font-semibold text-cyan-200">
                      ⏱️ Temps
                    </span>
                    <span className="rounded-xl border border-amber-400/20 bg-amber-500/10 px-3 py-2 text-[12px] font-semibold text-amber-200">
                      💡 Indices
                    </span>
                    <span className="rounded-xl border border-red-400/20 bg-red-500/10 px-3 py-2 text-[12px] font-semibold text-red-200">
                      📌 Réponses
                    </span>
                    <span className="rounded-xl border border-rose-400/20 bg-rose-500/10 px-3 py-2 text-[12px] font-semibold text-rose-200">
                      ❌ Erreurs
                    </span>
                  </div>
                </div>

                <div className="rounded-2xl border border-white/10 bg-black/30 p-4">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400">
                    Détail
                  </p>
                  <p className="mt-2 text-[13px] text-slate-300 leading-relaxed">
                    Ouvre “Système de points” en bas de page pour une explication détaillée de la méthode de calcul des points.
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur p-5">
              <p className="text-[11px] uppercase tracking-[0.22em] text-slate-400">
                Liens
              </p>
              <div className="mt-3 flex flex-col gap-2">
                <Link
                  to="/scoreboards"
                  className="rounded-2xl px-4 py-2 text-sm font-semibold bg-white/5 border border-white/10 hover:bg-white/10 transition"
                >
                  Leaderboard global
                </Link>
              </div>
            </div>
          </aside>
        </div>
        <div className="mt-6 rounded-3xl border border-white/10 bg-white/5 backdrop-blur p-5">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <p className="text-[11px] uppercase tracking-[0.22em] text-slate-400">
                Système de points
              </p>
              <p className="mt-1 text-sm text-slate-300">
                Classement trié sur les points stockés en base. Les métriques
                affichées (indices / réponses / erreurs) servent au détail.
              </p>
            </div>

            <details className="rounded-2xl border border-white/10 bg-black/30 px-4 py-3">
              <summary className="cursor-pointer text-sm font-semibold text-slate-200">
                Voir le rappel
              </summary>
              <div className="mt-3 text-[13px] text-slate-300 space-y-2">
                {formulaText.map((line, i) => (
                  <p key={i} className="leading-relaxed">
                    • {line}
                  </p>
                ))}
              </div>
            </details>
          </div>
        </div>
      </div>
    </div>
  );
}
